/**
 * Contracts view of the Options Tape: one row per contract, premium summed over every matching
 * print, with the Bought/Sold and structure verdicts the old Top Premium Board showed.
 * Colour follows the verdict: bought = green, sold = red, two-sided or unknown = neutral.
 */

import React from 'react';
import { formatPremium } from '../../utils/flowTapeApi';
import type { ContractRow } from './contractBoard';

export interface ContractsTableProps {
  rows: ContractRow[];
  loadedCount: number;
  loading: boolean;
  favs: Set<string>;
  onToggleFav: (id: string) => void;
  onOpen: (row: ContractRow) => void;
}

function rowClass(c: ContractRow): 'buy' | 'sell' | 'unk' {
  if (c.verdict.direction === 'Bought') return 'buy';
  if (c.verdict.direction === 'Sold') return 'sell';
  return 'unk';
}

const ContractsTable: React.FC<ContractsTableProps> = ({ rows, loadedCount, loading, favs, onToggleFav, onOpen }) => (
  <>
    <div className="tablewrap">
      <table className="tbl">
        <thead>
          <tr>
            <th className="th-star" aria-label="Favourite" />
            <th>Ticker</th>
            <th>Type</th>
            <th className="num">Strike</th>
            <th>Expiry</th>
            <th className="num">DTE</th>
            <th className="num">Premium</th>
            <th className="num">Prints</th>
            <th className="num">Contracts</th>
            <th title="Share of premium that printed at the ask">Bought / Sold</th>
            <th title="Share of labelled volume that printed as a complex order">Structure</th>
          </tr>
        </thead>
        <tbody>
          {loading && !rows.length && (
            <tr>
              <td colSpan={11}>
                <div className="empty">Summing premium per contract…</div>
              </td>
            </tr>
          )}
          {!loading && !rows.length && (
            <tr>
              <td colSpan={11}>
                <div className="empty">
                  {loadedCount ? 'None of the top contracts pass the verdict, rules or favourites.' : 'No contracts match these filters.'}
                </div>
              </td>
            </tr>
          )}
          {rows.map((c) => {
            const cls = rowClass(c);
            const fav = favs.has(c.contract);
            const v = c.verdict;
            return (
              <tr
                key={c.contract}
                className={cls}
                tabIndex={0}
                title={`${c.ticker} $${c.strike} ${c.expiry} — open contract history`}
                onClick={() => onOpen(c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(c);
                  }
                }}
              >
                <td>
                  <button
                    type="button"
                    className="tstar"
                    aria-label={fav ? 'Remove from favourites' : 'Add to favourites'}
                    onClick={(e) => { e.stopPropagation(); onToggleFav(c.contract); }}
                  >
                    {fav ? '★' : '☆'}
                  </button>
                </td>
                <td className="c-t">{c.ticker}</td>
                <td>
                  <span className={`tbadge ${cls}`}>{c.optionType === 'P' ? 'Put' : 'Call'}</span>
                </td>
                <td className="mono c-strike num">${c.strike}</td>
                <td className="mono">{c.expiry}</td>
                <td className="mono num">{c.dte == null ? '—' : `${c.dte}d`}</td>
                <td className="mono num">{formatPremium(c.premium)}</td>
                <td className="mono num">{c.prints.toLocaleString()}</td>
                <td className="mono num">{c.contracts.toLocaleString()}</td>
                <td className="mono">
                  {v.direction ? (
                    <>
                      {v.direction}
                      {v.buyPct != null && <span className="na"> · {Math.round(v.buyPct)}% ask</span>}
                    </>
                  ) : (
                    <span className="na">—</span>
                  )}
                </td>
                <td className="mono">
                  {v.structure ? (
                    <>
                      {v.structure}
                      {v.multiPct != null && <span className="na"> · {Math.round(v.multiPct)}%</span>}
                    </>
                  ) : (
                    <span className="na">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <div className="ftpager">
      <span className="count">
        {loadedCount
          ? <>showing <b>{rows.length}</b> of the top <b>{loadedCount}</b> contracts by premium</>
          : 'no contracts'}
      </span>
    </div>
  </>
);

export default ContractsTable;
